import React, { useEffect, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import "./style.css";
import CardImage from "../../../assets/image/icons/CreditCard.png";
import Delete from "../../../assets/image/icons/delete.svg";
import payfound from "../../../assets/image/icons/payfound.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import { getTokenizationKey } from "../../../plugins/ApiHandler";
import { getFreeCardImageAsync, getCardImageFromNumber, getAlternativeCardImages } from "../../../utils/cardImageHelper";
import AddCardDetailsForm from "../../../components/AdminViews/AddCardDetailsForm";
import sendSwal from "../../../components/Swal/sendSwal";
import CustomSwal from "../../../components/Swal/CustomSwal";
import { LoaderComponent } from "../../../components/Icon/Index";
import showToast from "../../../components/Toast/Toster";
import { Typography } from "@mui/material";
import BlueButton from "../../../components/Button/BlueButton";
import WhiteButton from "../../../components/Button/WhiteButton";
import TruncatedText from "../../../components/TruncatedText";
import { useUserType } from "../../../contexts/UserTypeContext";
import { getItem, setItem } from "../../../utils/storage";
import { getGatewayForPaymentMethod } from "../../../utils/gatewayHelper";

// Component to handle image loading with fallbacks - ensures ALL cards show images
const CardImageWithFallback = ({ primaryImage, alternativeImages = [], defaultImage, alt }) => {
  const [imageSrc, setImageSrc] = useState(primaryImage || defaultImage);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Reset when primary image changes
    setImageSrc(primaryImage || defaultImage);
    setCurrentIndex(0);
    setHasError(false);
  }, [primaryImage, defaultImage]);

  const handleImageError = (e) => {
    if (hasError) return; // Prevent infinite loops
    
    // Try alternative images in order
    if (currentIndex < alternativeImages.length) {
      const nextImage = alternativeImages[currentIndex];
      if (nextImage) {
        setImageSrc(nextImage);
        setCurrentIndex(currentIndex + 1);
        return;
      }
    }
    
    // If all alternatives failed, use default
    if (e.target.src !== defaultImage) {
      setImageSrc(defaultImage);
      setHasError(true);
    }
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      className="card-image"
      onError={handleImageError}
      style={{ objectFit: "contain" }}
      loading="lazy"
    />
  );
};

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [loading, setLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState([]);
  const [activePlanDetails, setActivePlanDetails] = useState(undefined);
  const [billingDetails, setBillingDetails] = useState([]);
  const [customerVault, setCustomerVault] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [tokenData, setTokenData] = useState(null);
  
  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  
  useEffect(() => {
    const handleDateFormat = () => {
      if (DateDecode?.Format) {
        setDateFormat(DateDecode?.Format);
      } else {
        setDateFormat("MM/DD/YYYY");
      }
    };
    handleDateFormat();
  }, [DateDecode]);
  const fetchData = async () => {
    try {
      const authRes = await handleAuth(navigate, location);
      setDateDecode(authRes?.themes);
      setTokenData(authRes?.data);
      const companyId = getItem("CompanyId");
      const cardGateway = getGatewayForPaymentMethod(authRes?.data, "Credit/debit card");
      const res = await AxiosInstance.post(
        "/v1/nmi/get-billing-customer-vault",
        {
          CompanyId: companyId,
          type: "card",
          Gateway: cardGateway,
          ...((cardGateway || "").toString().toUpperCase() === "DEJAVOO" ? { CustomerId: "Company" } : {}),
        }
      );
      if (
        res?.data?.status === 200 &&
        res?.data?.data &&
        res?.data?.data?.customer &&
        res?.data?.data?.customer?.billing
      ) {
        const billingData = [];
        setCustomerVault(res?.data?.data?.customer["@attributes"]?.id);
        if (Array.isArray(res?.data?.data?.customer?.billing)) {
          for (const elem of res?.data?.data?.customer?.billing) {
            // Get card number and type from billing data - try multiple fields
            const cardNumber = elem?.cc_number || 
                             elem?.["@attributes"]?.cc_number || 
                             elem?.ccnumber ||
                             elem?.["@attributes"]?.ccnumber ||
                             "";
            const cardType = elem?.cc_type || 
                           elem?.["@attributes"]?.cc_type ||
                           elem?.cardType ||
                           "";
            
            // Get card image using card number (most reliable) and card type
            const image = await getCardImage(cardType, cardNumber);
            
            // Get alternative images for fallback
            const alternativeImages = getAlternativeCardImages(cardType, cardNumber) || [];
            
            billingData.push({ 
              ...elem, 
              image: image || CardImage,
              alternativeImages: alternativeImages
            });
          }
        } else {
          const billing = res?.data?.data?.customer?.billing;
          // Get card number and type from billing data - try multiple fields
          const cardNumber = billing?.cc_number || 
                           billing?.["@attributes"]?.cc_number || 
                           billing?.ccnumber ||
                           billing?.["@attributes"]?.ccnumber ||
                           "";
          const cardType = billing?.cc_type || 
                         billing?.["@attributes"]?.cc_type ||
                         billing?.cardType ||
                         "";
          
          // Get card image using card number and card type
          const image = await getCardImage(cardType, cardNumber);
          
          // Get alternative images for fallback
          const alternativeImages = getAlternativeCardImages(cardType, cardNumber) || [];
          
          billingData.push({
            ...billing,
            image: image || CardImage,
            alternativeImages: alternativeImages
          });
        }
        setBillingDetails(billingData);
        // Auto-select logic: maintain selection by ID or select newly added card
        setSelectedCard((prevSelectedCard) => {
          if (billingData.length === 0) {
            return null;
          }
          if (billingData.length === 1) {
            // Only one card, select it
            return billingData[0];
          }
          // Multiple cards - try to maintain previous selection by ID
          if (prevSelectedCard && prevSelectedCard["@attributes"]?.id) {
            const selectedId = prevSelectedCard["@attributes"].id;
            const foundCard = billingData.find(card => card["@attributes"]?.id === selectedId);
            if (foundCard) {
              return foundCard;
            }
          }
          // If no previous selection or previous card not found, select the last card (newly added)
          return billingData[billingData.length - 1];
        });
      } else {
        setBillingDetails([]);
        setSelectedCard(null);
      }
      const response = await AxiosInstance.get(
        `/v1/planpurchase/company_plans`
      );
      if (response?.data?.statusCode === 200) {
        setPlanDetails(response?.data?.data);
        setActivePlanDetails(response?.data?.activePlan);
      }
    } catch (error) {
      console.error("Error: ", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCardImage = async (cardType, cardNumber) => {
    try {
      // Use free card image API - no rate limits, supports all card types worldwide
      let imageUrl = null;
      
      // ALWAYS try card number first (most reliable, works even if masked)
      if (cardNumber && /\d/.test(cardNumber)) {
        imageUrl = await getCardImageFromNumber(cardNumber);
        if (imageUrl) return imageUrl;
      }
      
      // Try to get image from card type
      if (cardType) {
        imageUrl = await getFreeCardImageAsync(cardType, cardNumber);
        if (imageUrl) return imageUrl;
      }
      
      // Final fallback
      return CardImage;
    } catch (error) {
      console.error("Error getting card image:", error.message);
      return CardImage;
    }
  };

  const handleDelete = async (item) => {
    sendSwal(isDonationMode).then(async (willDelete) => {
      if (willDelete) {
        try {
          if (item["@attributes"].id) {
            const object = {
              CompanyId: getItem("CompanyId"),
              customer_vault_id: customerVault,
              billing_id: item["@attributes"].id,
            };
            if (billingDetails.length > 1) {
              var url = `/v1/nmi/delete-customer-billing`;
            } else {
              url = `/v1/nmi/delete-customer-vault`;
            }

            const deleteRes = await AxiosInstance.post(url, object);
            if (deleteRes?.data?.status === 200) {
              showToast.success("Card deleted successfully");
              // Clear selection if the deleted card was selected
              if (selectedCard && selectedCard["@attributes"]?.id === item["@attributes"]?.id) {
                setSelectedCard(null);
              }
              fetchData();
            }
          }
        } catch (error) {
          console.error(error.message);
        }
      }
    });
  };

  const [modalShow, setModalShow] = useState(false);
  const [isPublicKeyAvailable, setIsPublicKeyAvailable] = useState(false);

  const getNmiKey = async (Company_Id) => {
    try {
      const keyResponse = await getTokenizationKey(Company_Id);
      if (keyResponse?.PublicKey) {
        setIsPublicKeyAvailable(true);
      } else {
        setIsPublicKeyAvailable(false);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const CompanyId = getItem("CompanyId");
      await getNmiKey(CompanyId);
    };
    fetchData();
  }, []);

  const cardGateway = getGatewayForPaymentMethod(tokenData, "Credit/debit card");
  const isDejavooGateway =
    (cardGateway || "").toString().toUpperCase() === "DEJAVOO" ||
    (billingDetails?.length > 0 && billingDetails?.some((c) => (c?.gateway || "").toString().toUpperCase() === "DEJAVOO"));
  const showAddCardButton = isPublicKeyAvailable || isDejavooGateway;
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  const handleCancelAccount = async (reason) => {
    try {
      const res = await AxiosInstance.delete(`/v1/planpurchase/cancel_plan`);

      if (res?.data?.statusCode === 200) {
        showToast.success(res?.data?.message);
        setItem("adminToken", res?.data?.token);
        navigate(`/auth/login`);
      } else {
        showToast.error(
          "There was an issue with account cancellation. Please try again later."
        );
      }
    } catch (error) {
      showToast.error(
        "There was an issue with account cancellation. Please try again later."
      );
    }
  };

  return (
    <>
      {loading ? (
        <div className={`account-container ${isDonationMode ? "donor-mode" : ""}`} style={{ padding: "20px 0" }}>
          <LoaderComponent
            variant="skeleton"
            skeletonLayout="form"
            isDonationMode={isDonationMode}
            height="50"
            width="50"
            loader={loading}
          />
        </div>
      ) : (
        <div className={`account-container ${isDonationMode ? "donor-mode" : ""}`} style={{ padding: "20px 0" }}>
          <div className="account-header" style={{ marginBottom: "20px" }}>
            <Typography className="account-title heading-three" style={{ fontSize: "25px", marginBottom: "8px" }}>
              Account & Billing
            </Typography>
            <Typography className="account-subtitle" style={{ fontSize: "14px", marginBottom: 0 }}>
              Manage your subscription, payment methods, and account settings
            </Typography>
          </div>

          <div className="account-content" style={{ gap: "20px" }}>
            <div className="account-top-section" style={{ gap: "20px", marginBottom: "20px" }}>
              <div className="account-overview-card" style={{ padding: "30px" }}>
                <div className="card-header-modern" style={{ marginBottom: "15px" }}>
                  <div className="card-title-section">
                    <Typography className="card-title-modern heading-five" style={{ fontSize: "20px", marginBottom: "4px" }}>
                      Account Overview
                    </Typography>
                    <Typography className="card-subtitle" style={{ fontSize: "13px", marginBottom: 0 }}>
                      Your subscription details and account information
                    </Typography>
                  </div>
                  <BlueButton
                    isDonationMode={isDonationMode}
                    className="modern-action-btn"
                    onClick={() => {
                      const plan = planDetails.find(
                        (item) => item?.status && item?.plan !== "Trial"
                      );
                      navigate(`/${CompanyUrl}/plan`, {
                        state: {
                          navigats: [...location.state.navigats, "/plan"],
                          plan,
                        },
                      });
                    }}
                    label="View Plan"
                  />
                </div>
                <div className="overview-data-section">
                  {planDetails && planDetails.length > 0 ? (
                    <div className="plan-table-container">
                      <table className="plan-details-table">
                        <thead>
                          <tr>
                            <th>Account Since</th>
                            <th>Account Status</th>
                            <th>Current Plan</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planDetails?.map((item, index) => (
                            <tr key={index}>
                              <td className="plan-date">
                                {formatDateWithUserTimezone(item?.date, {
                                  dateFormat: dateFormat,
                                  includeSeconds: false
                                })}
                              </td>
                              <td className="plan-status-cell">
                                <span className={`status-badge ${item?.status ? 'active' : 'inactive'}`}>
                                  {item?.status ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="plan-name">
                                <TruncatedText
                                  text={item?.plan || "plan not available"}
                                  className="text-blue-color"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data-state">
                      <div className="no-data-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div className="no-data-text">
                        No plan information available
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="billing-details-card" style={{ padding: "30px" }}>
                <div className="card-header-modern" style={{ marginBottom: "15px" }}>
                  <div className="card-title-section">
                    <Typography className="card-title-modern heading-five" style={{ fontSize: "20px", marginBottom: "4px" }}>
                      Billing Details
                    </Typography>
                    <Typography className="card-subtitle" style={{ fontSize: "13px", marginBottom: 0 }}>
                      Manage your payment methods and billing information
                    </Typography>
                  </div>
                </div>

                <div className="billing-content" style={{ marginTop: "10px" }}>
                  {billingDetails && billingDetails.length > 0 ? (
                    <div className="payment-cards-container">
                      {billingDetails?.map((item, index) => {
                        const isSelected = selectedCard && selectedCard["@attributes"]?.id === item["@attributes"]?.id;
                        const cardExp = item?.cc_exp || item?.["@attributes"]?.cc_exp || "";
                        const formattedExp = cardExp.length === 4 
                          ? `${cardExp.slice(0, 2)}/${cardExp.slice(2, 4)}`
                          : cardExp || "N/A";
                        
                        return (
                          <div 
                            key={index} 
                            className={`payment-card-row ${isSelected ? 'selected' : ''}`}
                            onClick={(e) => {
                              // Don't trigger selection if clicking on delete button
                              if (!e.target.closest('.delete-card-btn')) {
                                setSelectedCard(item);
                              }
                            }}
                          >
                            <div className="card-radio-container">
                              <input
                                type="radio"
                                className="payment-card-radio"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCard(item);
                                  } else {
                                    setSelectedCard(null);
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCard(item);
                                }}
                              />
                            </div>
                            <div className="card-logo-container">
                              <CardImageWithFallback
                                primaryImage={item?.image}
                                alternativeImages={item?.alternativeImages || []}
                                defaultImage={CardImage}
                                alt="Card"
                              />
                            </div>
                            <div className="card-details-container">
                              <div className="card-number-text">
                                {item?.cc_number || "4xxxxxxxx4242"}
                              </div>
                              <div className="card-expiry-text">
                                {(item?.gateway || "").toString().toUpperCase() === "DEJAVOO"
                                  ? "Credit"
                                  : formattedExp && formattedExp !== "N/A"
                                    ? `Credit - Valid Thru : ${formattedExp}`
                                    : "Credit"}
                              </div>
                            </div>
                            <div className="card-delete-container">
                              <div 
                                className="delete-card-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // If deleting the selected card, clear selection
                                  if (isSelected) {
                                    setSelectedCard(null);
                                  }
                                  handleDelete(item);
                                }}
                              >
                                <img src={Delete} alt="Delete" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-card-state">
                      <div className="no-card-icon">
                        <img src={CardImage} alt="No Card" />
                      </div>
                      <div className="no-card-content">
                        <div className="no-card-title" style={{ fontSize: "16px", marginBottom: "4px" }}>No Credit/Debit Card</div>
                        <div className="no-card-subtitle" style={{ fontSize: "13px" }}>
                          Continue using CMS by choosing a plan
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="billing-actions" style={{ marginTop: "15px", gap: "10px" }}>
                    <WhiteButton
                      className="choose-plan-btn"
                      onClick={() => {
                        const plan = planDetails.find(
                          (item) => item?.status && item?.plan !== "Trial"
                        );
                        if (selectedCard) {
                          const isDejavooCard = (selectedCard?.gateway || "").toString().toUpperCase() === "DEJAVOO";
                          const cardPayload = {
                            ...selectedCard,
                            CompanyId: getItem("CompanyId"),
                            customer_vault_id: customerVault,
                            ...(isDejavooCard
                              ? {
                                  ...(tokenData
                                    ? {
                                        first_name: tokenData.FirstName ?? selectedCard.first_name,
                                        last_name: tokenData.LastName ?? selectedCard.last_name,
                                        address_1: tokenData.Address ?? selectedCard.address_1,
                                        city: tokenData.City ?? selectedCard.city,
                                        state: tokenData.State ?? selectedCard.state,
                                        country: tokenData.Country ?? selectedCard.country,
                                        postal_code: tokenData.Zip ?? selectedCard.postal_code,
                                      }
                                    : {}),
                                  cc_exp: selectedCard.cc_exp || (selectedCard.expiryDate ? String(selectedCard.expiryDate).replace("/", "") : undefined),
                                }
                              : {}),
                          };
                          navigate(`/${CompanyUrl}/plan`, {
                            state: {
                              navigats: [...location.state.navigats, "/plan"],
                              card: cardPayload,
                              plan,
                            },
                          });
                        } else {
                          navigate(`/${CompanyUrl}/plan`, {
                            state: {
                              navigats: [...location.state.navigats, "/plan"],
                              plan,
                            },
                          });
                        }
                      }}
                      label="Choose Plan"
                    />
                    
                    {showAddCardButton && (
                      <BlueButton
                        isDonationMode={isDonationMode}
                        className="add-card-btn"
                        onClick={() => setModalShow(true)}
                        label={
                          <>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>Add Card</span>
                          </>
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="account-bottom-section" style={{ gap: "20px" }}>
              <div className="payment-history-card" style={{ padding: "30px" }}>
                <div className="card-header-modern" style={{ marginBottom: "15px" }}>
                  <div className="card-title-section">
                    <Typography className="card-title-modern heading-five" style={{ fontSize: "20px", marginBottom: "4px" }}>
                      Payment History
                    </Typography>
                    <Typography className="card-subtitle" style={{ fontSize: "13px", marginBottom: 0 }}>
                      View your transaction history and payment details
                    </Typography>
                  </div>
                </div>

                {(() => {
                  // Filter plan purchases that have transaction IDs (exclude Trial without SubscriptionId)
                  const paymentHistory = planDetails?.filter(
                    (item) => item?.SubscriptionId && item?.plan !== "Trial"
                  ) || [];

                  if (paymentHistory.length === 0 && activePlanDetails?.PlanName === "Trial") {
                    return (
                      <div className="trial-state">
                        <div className="trial-icon">
                          <img src={payfound} alt="Trial" />
                        </div>
                        <div className="trial-content">
                          <Typography className="trial-title" style={{ fontSize: "16px", marginBottom: "4px" }}>
                            No Payment History
                          </Typography>
                          <Typography className="trial-subtitle" style={{ fontSize: "13px" }}>
                            Once your trial ends, your payment history will be shown here.
                          </Typography>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="payment-details">
                      {/* Column Headers */}
                      <div className="payment-header">
                        <div className="header-label">Transaction ID</div>
                        <div className="header-label">Plan</div>
                        <div className="header-label">Amount</div>
                      </div>
                      {/* Payment Data - Show all plan purchases */}
                      {paymentHistory.map((item, index) => (
                        <div key={index} className="payment-info">
                          <div className="transaction-id">
                            {item?.SubscriptionId || "-"}
                          </div>
                          <div className="plan-name">
                            <TruncatedText
                              text={item?.plan || item?.purchaseData?.PlanName || "-"}
                              className="text-blue-color"
                              maxWords={5}
                            />
                          </div>
                          <div className="plan-amount">
                            {item?.purchaseData?.PlanPrice
                              ? `$${new Intl.NumberFormat("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(item.purchaseData.PlanPrice)}`
                              : "-"}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="cancel-account-card" style={{ padding: "30px" }}>
                <div className="card-header-modern" style={{ marginBottom: "15px" }}>
                  <div className="card-title-section">
                    <Typography className="card-title-modern heading-five" style={{ fontSize: "20px", marginBottom: "4px" }}>
                      Cancel Your Account
                    </Typography>
                    <Typography className="card-subtitle" style={{ fontSize: "13px", marginBottom: 0 }}>
                      Manage your subscription and account cancellation
                    </Typography>
                  </div>
                  {activePlanDetails?.PlanName !== "Trial" && (
                    <BlueButton
                      isDonationMode={isDonationMode}
                      className="modern-action-btn"
                      onClick={() => {
                        setIsCancelModalOpen(true);
                      }}
                      label="Cancel Account"
                    />
                  )}
                </div>

                {activePlanDetails?.PlanName === "Trial" ? (
                  <div className="trial-cancel-info" style={{ fontSize: "13px", padding: "10px 0" }}>
                    Your trial will automatically expire so there's no need to cancel
                  </div>
                ) : (
                  <div className="account-details" style={{ marginTop: "10px" }}>
                    <div className="account-info" style={{ gap: "12px" }}>
                      <div className="info-item" style={{ marginBottom: "12px" }}>
                        <div className="info-label" style={{ fontSize: "13px", marginBottom: "4px" }}>Current Plan</div>
                        <div className="info-value" style={{ fontSize: "14px" }}>
                          <TruncatedText
                            text={activePlanDetails?.PlanName || "PlanName not available"}
                            className="text-blue-color"
                          />
                        </div>
                      </div>
                      <div className="info-item" style={{ marginBottom: "12px" }}>
                        <div className="info-label" style={{ fontSize: "13px", marginBottom: "4px" }}>Plan Details</div>
                        <div className="info-value" style={{ fontSize: "14px" }}>
                          <TruncatedText
                            text={activePlanDetails?.PlanDetail || "Plan Detail not available"}
                            className="text-blue-color"
                          />
                        </div>
                      </div>
                      <div className="info-item" style={{ marginBottom: "12px" }}>
                        <div className="info-label" style={{ fontSize: "13px", marginBottom: "4px" }}>Plan Duration</div>
                        <div className="info-value" style={{ fontSize: "14px" }}>
                          {activePlanDetails?.dueDate
                            ? formatDateWithUserTimezone(activePlanDetails?.dueDate, {
                                dateFormat: dateFormat,
                                includeSeconds: false
                              })
                            : "Due Date not available"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AddCardDetailsForm
        onHide={() => setModalShow(false)}
        show={modalShow}
        fetchData={fetchData}
        CompanyId={tokenData?.CompanyId}
        Gateway={cardGateway || tokenData?.Gateway}
        CustomerId={(cardGateway || "").toString().toUpperCase() === "DEJAVOO" ? "Company" : undefined}
      />

      <CustomSwal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelAccount}
        isDonationMode={isDonationMode}
        title="Are you sure?"
        text="Once Cancel, you will not be able to recover!"
        confirmButtonText="Cancel"
        cancelButtonText="Cancel"
        placeholder="Enter reason for cancellation"
        requireInput={true}
      />
    </>
  );
};

export default Account;